/**
 * SeasonalSky — SEASON-1. The season's painted sky, in place of the
 * Googie gradient while Seasonal themes is on (GoogieShell switches to
 * it).
 *
 * The scenes are the holiday pack's own — ChristmasSky and HalloweenSky,
 * one painting per time of day — fully animated on the web (Rennan's
 * call: iOS shows the same paintings still, with native snow and bats
 * on top). Each painting is a 1180 × 1060 canvas of markup in
 * ./scenes, generated from the pack; only the one for the current
 * season and hour is ever downloaded.
 *
 * They're fetched as JSON assets, not `import()`ed: a failed dynamic
 * import fires `vite:preloadError`, which main.tsx answers with a hard
 * reload (DEPLOY-1) — and since the sky asks for its painting on every
 * load, one unreachable file (offline, a stale deploy) would reload the
 * app forever. A failed fetch just leaves the season's ground showing.
 *
 * The canvas is laid out the way the pack lays it out: scaled to cover
 * and anchored to the bottom centre, so the ground is always in view
 * and a tall window shows the middle of the painting. When the hour
 * turns, the new painting fades in over the old one (its own `skyIn`),
 * and the old one leaves once it's covered.
 *
 * A wash darkens each painting a little — more for the bright midday
 * snow — so ivory type and glass cards stay readable over it. Same
 * strengths as iOS.
 */
import { memo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { Tod } from '@/lib/googie';
import type { Season } from '@/lib/seasons';
import christmasDawn from './scenes/christmas-dawn.json?url';
import christmasDusk from './scenes/christmas-dusk.json?url';
import christmasMidday from './scenes/christmas-midday.json?url';
import christmasNight from './scenes/christmas-night.json?url';
import halloweenDawn from './scenes/halloween-dawn.json?url';
import halloweenDusk from './scenes/halloween-dusk.json?url';
import halloweenMidday from './scenes/halloween-midday.json?url';
import halloweenNight from './scenes/halloween-night.json?url';

interface Scene {
  html: string;
  css: string;
}

type SceneKey = `${Season}-${Tod}`;

const SCENE_URLS: Record<SceneKey, string> = {
  'halloween-dawn': halloweenDawn,
  'halloween-midday': halloweenMidday,
  'halloween-dusk': halloweenDusk,
  'halloween-night': halloweenNight,
  'christmas-dawn': christmasDawn,
  'christmas-midday': christmasMidday,
  'christmas-dusk': christmasDusk,
  'christmas-night': christmasNight,
};

/** Paintings already downloaded this session. */
const loaded = new Map<SceneKey, Scene>();

function loadScene(key: SceneKey): Promise<Scene> {
  const hit = loaded.get(key);
  if (hit) return Promise.resolve(hit);
  return fetch(SCENE_URLS[key])
    .then((response) => {
      if (!response.ok) throw new Error(`seasonal sky ${key}: ${response.status}`);
      return response.json() as Promise<Partial<Scene>>;
    })
    .then((data) => {
      if (typeof data.html !== 'string' || typeof data.css !== 'string') throw new Error(`seasonal sky ${key}: malformed`);
      const scene = { html: data.html, css: data.css };
      loaded.set(key, scene);
      return scene;
    });
}

/** The canvas the pack paints on. */
const CANVAS_W = 1180;
const CANVAS_H = 1060;

/** The ground under each painting (the pack's own), while it loads. */
const GROUND: Record<Season, string> = {
  halloween: '#0A0818',
  christmas: '#0B1512',
};

/** How much each painting steps back behind content (iOS's numbers). */
const WASH: Record<Season, Record<Tod, number>> = {
  christmas: { midday: 0.46, dawn: 0.36, dusk: 0.3, night: 0.22 },
  halloween: { midday: 0.26, dawn: 0.24, dusk: 0.18, night: 0.14 },
};

function wash(season: Season, tod: Tod): string {
  const base = WASH[season][tod];
  // The Christmas ground is snow: the foot needs more.
  const foot = Math.min(0.9, base + (season === 'christmas' ? 0.22 : 0.12));
  return `linear-gradient(180deg,rgba(0,0,0,${(base * 0.55).toFixed(3)}) 0%,rgba(0,0,0,${base}) 45%,rgba(0,0,0,${foot.toFixed(3)}) 100%)`;
}

export function SeasonalSky({ season, tod }: { season: Season; tod: Tod }) {
  const key: SceneKey = `${season}-${tod}`;
  const [layers, setLayers] = useState<Array<{ key: SceneKey; scene: Scene }>>(() => {
    const hit = loaded.get(key);
    return hit ? [{ key, scene: hit }] : [];
  });

  // Fetch the painting for this season and hour, then lay it on top.
  useEffect(() => {
    let cancelled = false;
    const show = (scene: Scene) => {
      if (cancelled) return;
      // On top, always — even if it's the painting underneath that's
      // still fading out (A → B → A inside the fade), or the timer below
      // would keep the wrong one. One painting under it, at most.
      setLayers((prev) =>
        prev[prev.length - 1]?.key === key
          ? prev
          : [...prev.filter((layer) => layer.key !== key).slice(-1), { key, scene }],
      );
    };
    loadScene(key)
      .then(show)
      .catch(() => {
        /* offline, or a stale deploy — the season's ground still shows */
      });
    return () => {
      cancelled = true;
    };
  }, [key]);

  // The old painting leaves once the new one has faded in over it.
  useEffect(() => {
    if (layers.length < 2) return;
    const id = window.setTimeout(() => setLayers((prev) => prev.slice(-1)), 1800);
    return () => window.clearTimeout(id);
  }, [layers]);

  // Cover the window, like the pack's own fit().
  const rootRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const fit = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (!w || !h) return;
      setScale(Math.max(w / CANVAS_W, h / CANVAS_H));
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={rootRef}
      aria-hidden
      data-g-season-sky={season}
      className="pointer-events-none fixed inset-0 overflow-hidden"
      style={{ background: GROUND[season], contain: 'strict' }}
    >
      {layers.map((layer) => (
        <div
          key={layer.key}
          style={{
            position: 'absolute',
            left: '50%',
            bottom: 0,
            width: CANVAS_W,
            height: CANVAS_H,
            translate: '-50% 0',
            transformOrigin: '50% 100%',
            scale: String(scale),
          }}
        >
          <Painting scene={layer.scene} />
        </div>
      ))}
      <div className="absolute inset-0" style={{ background: wash(season, tod) }} />
    </div>
  );
}

/** One painting. Memoised on the scene, so a resize only rescales the
 *  frame around it and never re-parses 100 KB of markup. The markup is
 *  the pack's own, generated at build time — no user content reaches it. */
const Painting = memo(function Painting({ scene }: { scene: Scene }) {
  return (
    <>
      <style>{scene.css}</style>
      <div style={{ position: 'absolute', inset: 0 }} dangerouslySetInnerHTML={{ __html: scene.html }} />
    </>
  );
});

export default SeasonalSky;
